import { Button, Modal, Text } from '@viapeople/components'
import { useState } from 'react'

interface RatingDistributionItem {
	rating: number
	count: number
	percentage: number
}

interface RatingStats {
	mode: number | null
	median: number | null
	average: number | null
	min: number | null
	max: number | null
	count: number
}

interface ParticipantWithRatings {
	userId: string
	firstName: string
	lastName: string
	email: string
	ratings: number[]
	stats: RatingStats
}

interface RatingConfigOption {
	label: string
	value: string | number
}

interface RatingConfig {
	options?: RatingConfigOption[]
}

interface PecAnonymousHeatmapProps {
	ratingDistribution: RatingDistributionItem[]
	ratingScale: number
	ratingConfig?: RatingConfig | null
	cycleAverage: number
	overallStats: RatingStats
	participants: ParticipantWithRatings[]
}

// Get rating bounds from config or fallback to 1-N
const getRatingBounds = (
	ratingConfig: RatingConfig | null | undefined,
	scale: number,
): { min: number; max: number } => {
	if (ratingConfig?.options?.length) {
		const values = ratingConfig.options.map((o) => Number(o.value)).filter((v) => !Number.isNaN(v))
		if (values.length > 0) {
			return { min: Math.min(...values), max: Math.max(...values) }
		}
	}
	return { min: 1, max: scale }
}

// Get color for rating bar based on rating value and scale bounds
const getBarColor = (rating: number, ratingConfig: RatingConfig | null | undefined, scale: number): string => {
	const { min, max } = getRatingBounds(ratingConfig, scale)
	// Calculate percentage based on position in scale (min to max)
	const percentage = max === min ? 100 : ((rating - min) / (max - min)) * 100

	if (percentage >= 80) return 'bg-green-500'
	if (percentage >= 60) return 'bg-green-400'
	if (percentage >= 40) return 'bg-yellow-400'
	if (percentage >= 20) return 'bg-orange-400'
	return 'bg-red-400'
}

// Get color for rating tile
const getRatingTileColor = (rating: number, ratingConfig: RatingConfig | null | undefined, scale: number): string => {
	const { min, max } = getRatingBounds(ratingConfig, scale)
	// Calculate percentage based on position in scale (min to max)
	const percentage = max === min ? 100 : ((rating - min) / (max - min)) * 100

	if (percentage >= 80) return 'bg-green-500 text-white'
	if (percentage >= 60) return 'bg-green-400 text-white'
	if (percentage >= 40) return 'bg-yellow-400 text-yellow-900'
	if (percentage >= 20) return 'bg-orange-400 text-white'
	return 'bg-red-400 text-white'
}

// Nickel rounding - round to nearest 0.05
const nickelRound = (value: number): number => {
	return Math.round(value * 20) / 20
}

// Format stat value with nickel rounding (2 decimal places)
const formatStat = (value: number | null): string => {
	return value !== null ? nickelRound(value).toFixed(2) : '-'
}

// Format comparison value with color and nickel rounding
const formatComparison = (value: number | null, overallValue: number | null): { text: string; colorClass: string } => {
	if (value === null || overallValue === null) {
		return { text: '-', colorClass: 'text-gray-400' }
	}
	const diff = nickelRound(value - overallValue)
	const sign = diff > 0 ? '+' : ''
	const colorClass = diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-600'
	return { text: `${sign}${diff.toFixed(2)}`, colorClass }
}

export const PecAnonymousHeatmap = ({
	ratingDistribution,
	ratingScale,
	ratingConfig,
	cycleAverage,
	overallStats,
	participants,
}: PecAnonymousHeatmapProps) => {
	const [isFullscreen, setIsFullscreen] = useState(false)

	// Get the max rating value for display purposes
	const { max: maxRatingValue } = getRatingBounds(ratingConfig, ratingScale)
	// Find max count for scaling bars
	const maxCount = Math.max(...ratingDistribution.map((r) => r.count), 1)

	// Sort participants by average rating (highest first)
	const sortedParticipants = [...participants].sort((a, b) => {
		const avgA = a.stats.average ?? 0
		const avgB = b.stats.average ?? 0
		return avgB - avgA
	})

	if (participants.length === 0) {
		return <div className='text-center py-8 text-gray-500'>No participants in this cycle</div>
	}

	// Extracted heatmap content to render in both inline and fullscreen views
	const heatmapContent = (isFullscreenView: boolean) => (
		<div className={isFullscreenView ? 'space-y-8 overflow-auto h-full p-4' : 'space-y-8'}>
			{/* Rating Distribution Section */}
			<div className='bg-white border border-gray-200 rounded-lg p-6'>
				<div className='mb-4'>
					<Text variant='medium' weight='semibold' className='text-gray-900'>
						Rating Distribution
					</Text>
					<Text variant='small' className='text-gray-600 mt-1'>
						How ratings are distributed across all participants
					</Text>
				</div>

				<div className='space-y-3'>
					{ratingDistribution.map((item) => (
						<div key={item.rating} className='flex items-center gap-4'>
							{/* Rating label */}
							<div className='w-8 text-right'>
								<Text variant='small' weight='semibold' className='text-gray-700'>
									{item.rating}
								</Text>
							</div>

							{/* Bar */}
							<div className='flex-1 bg-gray-100 rounded-full h-8 overflow-hidden'>
								<div
									className={`h-full rounded-full transition-all duration-300 flex items-center ${getBarColor(item.rating, ratingConfig, ratingScale)}`}
									style={{
										width: `${(item.count / maxCount) * 100}%`,
										minWidth: item.count > 0 ? '2rem' : '0',
									}}>
									{item.count > 0 && <span className='text-white text-xs font-semibold px-2'>{item.count}</span>}
								</div>
							</div>

							{/* Percentage */}
							<div className='w-16 text-right'>
								<Text variant='small' className='text-gray-600'>
									{item.percentage.toFixed(1)}%
								</Text>
							</div>
						</div>
					))}
				</div>

				{/* Summary */}
				<div className='mt-4 pt-4 border-t border-gray-200 flex items-center justify-between'>
					<Text variant='small' className='text-gray-600'>
						Total ratings: {ratingDistribution.reduce((sum, r) => sum + r.count, 0)}
					</Text>
					<div className='flex items-center gap-2'>
						<Text variant='small' className='text-gray-600'>
							Cycle Average:
						</Text>
						<span className='px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm'>
							{nickelRound(cycleAverage).toFixed(2)} / {maxRatingValue}
						</span>
					</div>
				</div>
			</div>

			{/* Participant Statistics Table */}
			<div className='bg-white border border-gray-200 rounded-lg p-6'>
				<div className='mb-4'>
					<Text variant='medium' weight='semibold' className='text-gray-900'>
						Participant Statistics
					</Text>
					<Text variant='small' className='text-gray-600 mt-1'>
						Detailed statistics for each participant with comparison to overall cycle
					</Text>
				</div>

				{/* Overall Statistics Header */}
				<div className='mb-4 p-4 bg-primary-50 border border-primary-200 rounded-lg'>
					<Text variant='small' weight='semibold' className='text-primary-800 mb-2'>
						Overall Cycle Statistics
					</Text>
					<div className='grid grid-cols-5 gap-4 text-sm'>
						<div>
							<span className='text-primary-600'>Mode:</span>{' '}
							<span className='font-semibold text-primary-900'>{formatStat(overallStats.mode)}</span>
						</div>
						<div>
							<span className='text-primary-600'>Median:</span>{' '}
							<span className='font-semibold text-primary-900'>{formatStat(overallStats.median)}</span>
						</div>
						<div>
							<span className='text-primary-600'>Average:</span>{' '}
							<span className='font-semibold text-primary-900'>{formatStat(overallStats.average)}</span>
						</div>
						<div>
							<span className='text-primary-600'>Min:</span>{' '}
							<span className='font-semibold text-primary-900'>{formatStat(overallStats.min)}</span>
						</div>
						<div>
							<span className='text-primary-600'>Max:</span>{' '}
							<span className='font-semibold text-primary-900'>{formatStat(overallStats.max)}</span>
						</div>
					</div>
				</div>

				{/* Participants Table */}
				<div className='overflow-x-auto'>
					<table className='min-w-full divide-y divide-gray-200'>
						<thead className='bg-gray-50'>
							<tr>
								<th className='px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10'>
									Participant
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Mode
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Median
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Average
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Min
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Max
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Mode vs Overall
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Median vs Overall
								</th>
								<th className='px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider'>
									Avg vs Overall
								</th>
								<th className='px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]'>
									All Ratings
								</th>
							</tr>
						</thead>
						<tbody className='bg-white divide-y divide-gray-200'>
							{sortedParticipants.map((participant) => {
								const modeComp = formatComparison(participant.stats.mode, overallStats.mode)
								const medianComp = formatComparison(participant.stats.median, overallStats.median)
								const avgComp = formatComparison(participant.stats.average, overallStats.average)

								return (
									<tr key={participant.userId} className='hover:bg-gray-50'>
										{/* Participant Name */}
										<td className='px-3 py-3 whitespace-nowrap sticky left-0 bg-white z-10'>
											<div>
												<Text variant='small' weight='semibold' className='text-gray-900'>
													{participant.firstName} {participant.lastName}
												</Text>
												<Text variant='xsmall' className='text-gray-500'>
													{participant.stats.count} rating{participant.stats.count !== 1 ? 's' : ''}
												</Text>
											</div>
										</td>

										{/* Mode */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<Text variant='small' className='text-gray-700'>
												{formatStat(participant.stats.mode)}
											</Text>
										</td>

										{/* Median */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<Text variant='small' className='text-gray-700'>
												{formatStat(participant.stats.median)}
											</Text>
										</td>

										{/* Average */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<Text variant='small' weight='semibold' className='text-gray-900'>
												{formatStat(participant.stats.average)}
											</Text>
										</td>

										{/* Min */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<Text variant='small' className='text-gray-700'>
												{formatStat(participant.stats.min)}
											</Text>
										</td>

										{/* Max */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<Text variant='small' className='text-gray-700'>
												{formatStat(participant.stats.max)}
											</Text>
										</td>

										{/* Mode vs Overall */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<span className='text-sm text-gray-700'>{formatStat(participant.stats.mode)}</span>
											<span className={`text-xs font-medium ${modeComp.colorClass} ml-1`}>({modeComp.text})</span>
										</td>

										{/* Median vs Overall */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<span className='text-sm text-gray-700'>{formatStat(participant.stats.median)}</span>
											<span className={`text-xs font-medium ${medianComp.colorClass} ml-1`}>({medianComp.text})</span>
										</td>

										{/* Avg vs Overall */}
										<td className='px-3 py-3 whitespace-nowrap text-center'>
											<span className='text-sm text-gray-700'>{formatStat(participant.stats.average)}</span>
											<span className={`text-xs font-medium ${avgComp.colorClass} ml-1`}>({avgComp.text})</span>
										</td>

										{/* All Ratings (colored tiles) */}
										<td className='px-3 py-3'>
											<div className='flex flex-wrap gap-1'>
												{participant.ratings.length > 0 ? (
													participant.ratings.map((rating, index) => (
														<span
															// biome-ignore lint/suspicious/noArrayIndexKey: ratings are numeric values that can repeat
															key={index}
															className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-semibold ${getRatingTileColor(rating, ratingConfig, ratingScale)}`}>
															{rating}
														</span>
													))
												) : (
													<Text variant='xsmall' className='text-gray-400 italic'>
														No ratings
													</Text>
												)}
											</div>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>

				{/* Legend */}
				<div className='mt-6 pt-4 border-t border-gray-200'>
					<Text variant='xsmall' weight='semibold' className='text-gray-600 mb-2'>
						Rating Color Legend
					</Text>
					<div className='flex flex-wrap gap-3'>
						<div className='flex items-center gap-2'>
							<span className='w-6 h-6 rounded bg-green-500 flex items-center justify-center text-white text-xs font-semibold'>
								5
							</span>
							<Text variant='xsmall' className='text-gray-600'>
								80%+ (Excellent)
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='w-6 h-6 rounded bg-green-400 flex items-center justify-center text-white text-xs font-semibold'>
								4
							</span>
							<Text variant='xsmall' className='text-gray-600'>
								60-79% (Good)
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='w-6 h-6 rounded bg-yellow-400 flex items-center justify-center text-yellow-900 text-xs font-semibold'>
								3
							</span>
							<Text variant='xsmall' className='text-gray-600'>
								40-59% (Average)
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='w-6 h-6 rounded bg-orange-400 flex items-center justify-center text-white text-xs font-semibold'>
								2
							</span>
							<Text variant='xsmall' className='text-gray-600'>
								20-39% (Below Avg)
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='w-6 h-6 rounded bg-red-400 flex items-center justify-center text-white text-xs font-semibold'>
								1
							</span>
							<Text variant='xsmall' className='text-gray-600'>
								Below 20%
							</Text>
						</div>
					</div>
				</div>
			</div>
		</div>
	)

	return (
		<div>
			{/* Expand to fullscreen button */}
			<div className='flex justify-end mb-4'>
				<Button variant='secondary' size='small' onClick={() => setIsFullscreen(true)}>
					⛶ Expand Full Screen
				</Button>
			</div>

			{/* Inline heatmap content */}
			{heatmapContent(false)}

			{/* Fullscreen modal */}
			<Modal
				isOpen={isFullscreen}
				onClose={() => setIsFullscreen(false)}
				title='Heat Map Results'
				fullScreen
				fullContent
				bodyClassName='bg-gray-50'>
				{heatmapContent(true)}
			</Modal>
		</div>
	)
}
